import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, Dialog, FormControlLabel, Step, StepLabel, TableRow, Tooltip } from "@mui/material";
import {
    ButtonRow,
    DialogContentSpacious,
    InfoText,
    ModalTitle,
    StepperStyled,
    StyledButton,
} from "../SharedModalStyled";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import { Product } from "../../../../data/entities";
import { api } from "../../../../shared/hooks/api";
import SearchBarWithSuggestion from "../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import TextBox from "../../../../shared/components/TextBox";
import { formatInternationalNumber, formatNumber, parseInternationalToNumber } from "../../../../utils/format";
import { formatDateVi } from "../../../../utils/dateUtils";
import Status from "../../../../shared/components/Status";
import { SALE_ORDER_STATUS } from "../../../../data/enums";
import { ProductOrderFilterType, valueToText } from "../../../../configs/filterTypes";
import { color } from "../../../../shared/styles";
import { TableWarningMessage } from "../../../ProductOrder/single/AddProductModal/Styled";
import IconButton from "../../../../shared/components/IconButton";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { error } from "../../../../utils/toast";

interface Props {
    open: boolean;
    onClose: () => void;
    isAllowToSearchProduct: boolean;
    initialProducts?: any[];
    lockedSaleOrder?: {
        id?: string;
        order_name?: string;
    };
}

const ProductTableColumnsStep1 = [
    { text: "Mã sản phẩm", icon: <></> },
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Tổng số lượng cần sản xuất", icon: <></> },
    { text: "Hành động", icon: <></> },
];

const ProductTableColumnsStep3 = [
    { text: "Mã sản phẩm", icon: <></> },
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Số lượng cần sản xuất", icon: <></> },
    { text: "", icon: <></> },
];

const getStatusLabel = (status: string) => valueToText(ProductOrderFilterType, "status", status) || status;

const getStatusTheme = (status: string) => {
    switch (status) {
        case SALE_ORDER_STATUS.LOGISTIC_REVIEWED:
            return "WARNING";
        case SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING:
            return "PENDING";
        default:
            return "DISABLE";
    }
};

const SaleOrderTableColumns = [
    { text: "Mã đơn hàng", icon: <></> },
    { text: "Khách hàng", icon: <></> },
    { text: "Trạng thái", icon: <></> },
    { text: "Ngày đến hạn", icon: <></> },
    { text: "Sản phẩm", icon: <></> },
];

const SaleOrderRow = ({ item, extraRowProps }: any) => {
    const { selectedIds, toggleRow } = extraRowProps;
    const isSelected = selectedIds.has(item.id);

    const handleSelectRow = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest("a")) return;
        toggleRow(item.id);
    };

    return (
        <TableRow
            onClick={handleSelectRow}
            sx={{
                backgroundColor: isSelected ? "#e3f2fd !important" : "transparent",
                cursor: "pointer",
                "&:hover": { backgroundColor: isSelected ? "#e3f2fd !important" : "#f5f5f5 !important" },
            }}
        >
            <StyledTableCell align="left">
                <input type="checkbox" checked={isSelected} readOnly />
            </StyledTableCell>
            <StyledTableCell align="left">
                <StyledNavLink to={`/product-orders/${item.id}`} target="_blank">
                    {item.order_name}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left">
                {item.customer_name ? (
                    <StyledNavLink to={`/customers/${item.customer_id}`} target="_blank">
                        {item.customer_name}
                    </StyledNavLink>
                ) : (
                    <span style={{ color: color.status.WARNING }}>{item.customer_name}</span>
                )}
            </StyledTableCell>
            <StyledTableCell align="left">
                <Status colorTheme={getStatusTheme(item.status)} text={getStatusLabel(item.status)} />
            </StyledTableCell>
            <StyledTableCell align="left">{formatDateVi(item.due_date)}</StyledTableCell>
            <StyledTableCell align="left">
                {(item.products || []).slice(0, 3).map((p: any, idx: number) => (
                    <div key={idx} style={{ display: "flex", gap: 6 }}>
                        <span>{p.product?.name || ""}</span>
                        <span style={{ color: "#666" }}>x{p.quantity}</span>
                    </div>
                ))}
            </StyledTableCell>
        </TableRow>
    );
};

export const applyProductionQuantities = (
    items: any[],
    productionMap: Record<string, number>,
    filterEmpty: boolean,
) => {
    const updated = items.map((p: any) => {
        const productionQuantity = Number(productionMap[p.id] || 0);
        return {
            ...p,
            production_quantity: productionQuantity,
        };
    });

    return filterEmpty ? updated.filter((p: any) => p.production_quantity > 0) : updated;
};

const ProductRowStep = ({ item, extraRowProps }: any) => {
    const editable = extraRowProps?.editable;
    const onRemove = extraRowProps?.onRemove;
    const onChangeQty = extraRowProps?.onChangeQty;
    const handleClickRow = extraRowProps?.handleClickRow;
    const minRequired = Number(item.min_required_quantity || 0);
    const currentQty = Number(item.production_quantity || 0);
    const isReducedBelowRequired = editable && minRequired > 0 && currentQty < minRequired;
    const warningCellStyle = isReducedBelowRequired ? { backgroundColor: color.status.DANGER_LIGHT } : undefined;

    if (item?.isSummary) {
        return (
            <TableRow>
                <StyledTableCell align="left" style={{ fontWeight: 700 }}>
                    {item.id}
                </StyledTableCell>
                <StyledTableCell align="left" style={{ fontWeight: 700 }}>
                    {item.name}
                </StyledTableCell>
                <StyledTableCell align="left"></StyledTableCell>
                <StyledTableCell align="left"></StyledTableCell>
            </TableRow>
        );
    }

    return (
        <TableRow>
            <StyledTableCell align="left" style={warningCellStyle}>
                <StyledNavLink to={`/products/${item.id}`}>
                    {item.product_code ? item.product_code : item.id}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left" style={warningCellStyle}>{item.name}</StyledTableCell>
            <StyledTableCell align="left" style={warningCellStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {editable ? (
                        <TextBox
                            numeric
                            style={isReducedBelowRequired ? {
                                backgroundColor: color.status.DANGER_LIGHT,
                                borderColor: color.status.WARNING,
                            } : undefined}
                            text={formatInternationalNumber(item.production_quantity ?? "")}
                            onChangeText={(value: string) => {
                                const n = parseInternationalToNumber(value, error);
                                if (!Number.isFinite(n)) return;
                                extraRowProps.onChangeQty(item.id, n);
                            }}
                            disabled={extraRowProps.isView}
                        />
                    ) : (
                        <span>{formatNumber(item.production_quantity || 0)}</span>
                    )}
                    {isReducedBelowRequired ? (
                        <span
                            style={{ color: color.status.WARNING, fontSize: 12, lineHeight: "16px", fontWeight: 700 }}>
                            Nhu cầu: {formatNumber(minRequired)}
                        </span>
                    ) : null}
                </div>
            </StyledTableCell>
            <StyledTableCell align="left" style={warningCellStyle}>
                {handleClickRow ? (
                    <StyledButton
                        variant="outlined"
                        onClick={() => handleClickRow(item)}
                        style={{
                            padding: "4px 16px",
                            fontSize: "13px",
                            minWidth: "auto",
                            fontWeight: 500,
                            height: "28px",
                            backgroundColor: "white",
                            borderColor: "#d0d0d0",
                            color: "#333",
                        }}
                    >
                        Chọn
                    </StyledButton>
                ) : onRemove ? (
                    <IconButton onClick={() => onRemove(item)}>
                        <DeleteIcon />
                    </IconButton>
                ) : null}
            </StyledTableCell>
        </TableRow>
    );
};

const CreateFromProductStepper = ({
    open,
    onClose,
    isAllowToSearchProduct,
    initialProducts,
    lockedSaleOrder,
}: Props) => {
    const navigate = useNavigate();
    const [active, setActive] = useState(lockedSaleOrder ? 2 : 0);

    const [{ data }, fetchProducts] = api.get(
        `/products?is_active=true&page_size=${Number.MAX_SAFE_INTEGER}`,
        undefined,
        true,
    );
    const [, findSaleOrdersByProducts] = api.post("/product-flow/v2/orders/find-sale-orders/");
    const [, generateMaterialRequest] = api.post("/product-flow/v2/orders/generate-material-request/");

    const products = useMemo(() => data?.results || [], [data?.results]);
    const [fillMaterials, setFillMaterials] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState<any[]>(initialProducts || []);
    const selectedProductsRef = useRef<any[]>(selectedProducts);
    useEffect(() => {
        selectedProductsRef.current = selectedProducts;
    }, [selectedProducts]);
    const [saleOrders, setSaleOrders] = useState<any[]>(
        lockedSaleOrder ? [lockedSaleOrder] : [],
    );
    const [selectedSaleOrderIds, setSelectedSaleOrderIds] = useState<
        Set<string | number>
    >(
        new Set(
            lockedSaleOrder?.id ? [lockedSaleOrder.id] : [],
        ),
    );
    useEffect(() => {
        if (open) {
            setSelectedProducts(initialProducts || []);
            if (lockedSaleOrder?.id) {
                setActive(2);
                setSaleOrders([lockedSaleOrder]);
                setSelectedSaleOrderIds(new Set([lockedSaleOrder.id]));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialProducts?.length]);



    // Step 1: keep production_quantity read-only, hydrate via aggregation
    const productIdsKey = useMemo(() => (selectedProducts || []).map((p: any) => p.id).sort().join(","), [selectedProducts]);
    useEffect(() => {
        const fetchAggregations = async () => {
            if (
                lockedSaleOrder ||
                !selectedProducts ||
                selectedProducts.length === 0
            ) {
                return;
            }
            try {
                const product_ids = selectedProducts.map((p: any) => p.id);
                const res = await findSaleOrdersByProducts({ product_ids });
                console.log("[MR] Step1 fetchAggregations res", res);
                const productionMap: Record<string, number> = res?.production_qty_by_product || {};
                console.log("[MR] Step1 productionMap", productionMap);
                setSelectedProducts(applyProductionQuantities(selectedProducts, productionMap, !!initialProducts));

            } catch (e) {
                console.error("Failed to fetch aggregations", e);
            }
        };
        fetchAggregations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productIdsKey, lockedSaleOrder]);

    const handleOnclickProduct = (item: Product, isAdded: boolean) => {
        if (!isAllowToSearchProduct) return;
        const exists = selectedProducts.some((p) => p.id === (item as any).id);
        if (isAdded) {
            // Allow only ONE product selection in step 1
            if (!exists) setSelectedProducts([{ ...item, production_quantity: 0 }]);
        } else if (!isAdded && exists) {
            setSelectedProducts([]);
        }
    };

    const handleClickOneProductAndGoNext = (item: Product) => {
        setSelectedProducts([item]);
        goNext([item]);
    };

    const removeProduct = (removed: any) => {
        if (!isAllowToSearchProduct) return;
        setSelectedProducts(selectedProducts.filter((p) => p.id !== removed.id));
    };

    const goNext = async (currentSelectedProducts?: Product[]) => {
        const products = currentSelectedProducts || selectedProducts;

        if (active === 0) {
            if (products.length === 0) return;
            try {
                const product_ids = products.map((p) => p.id);
                console.log("products 1", products);
                const res = await findSaleOrdersByProducts({
                    product_ids,
                    statuses: [SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING],
                });
                setSaleOrders(res?.sale_orders || []);

            } catch (e) {
                console.error("Failed to fetch sale orders", e);
            }
        }
        if (active === 1) {
            try {
                const product_ids = products.map((p) => p.id);
                const sale_order_ids = Array.from(selectedSaleOrderIds.values());
                const res = await findSaleOrdersByProducts({
                    product_ids,
                    statuses: [SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING],
                    sale_order_ids,
                });
                const productionMap: Record<string, number> = res?.production_qty_by_product || {};
                setSelectedProducts((prev) =>
                    applyProductionQuantities(prev, productionMap, false).map((p: any) => ({
                        ...p,
                        min_required_quantity: Number(productionMap[p.id] || 0),
                    })),
                );
            } catch (e) {
                console.error("Failed to fetch selected sale order production quantities", e);
                error("Không thể tính toán số lượng sản xuất");
                return;
            }

        }
        setActive((current) => Math.min(current + 1, 2));

    };

    const goBack = async () => {
        if (active - 1 <= 0) {
            // Restore full initial list (if provided); otherwise keep current selection
            const baseProducts = (initialProducts && initialProducts.length > 0) ? initialProducts : selectedProducts;
            setSelectedProducts(baseProducts || []);

            if (!baseProducts || baseProducts.length === 0) return;
            try {
                const product_ids = baseProducts.map((p: any) => p.id);
                const res = await findSaleOrdersByProducts({ product_ids });
                const productionMap: Record<string, number> = res?.production_qty_by_product || {};
                setSelectedProducts(applyProductionQuantities(baseProducts, productionMap, !!initialProducts));
            } catch (e) {
                console.error("Failed to fetch aggregations", e);
            }
        }
        setActive((current) => Math.max(current - 1, 0));
    };

    const finish = async () => {
        if (selectedSaleOrderIds.size === 0) {
            error("Vui lòng chọn đơn hàng");
            return;
        }

        const sale_order_ids = Array.from(selectedSaleOrderIds.values());
        const latestProducts = selectedProductsRef.current || [];
        const requested_products = latestProducts
            .map((p: any) => ({
                id: p.id,
                name: p.name,
                quantity: Math.max(0, Number(p.production_quantity || 0)),
            }))
            .filter((p: any) => p.quantity > 0);
        if (requested_products.length === 0) {
            error("Vui lòng nhập số lượng sản xuất lớn hơn 0");
            return;
        }

        const product_ids = requested_products.map((p: any) => p.id);
        let preselectedMaterials: any[] = [];
        const selectedOrders = saleOrders.filter((o) => selectedSaleOrderIds.has(o.id));
        const note = `Lãnh vật tư cho đơn hàng: ${selectedOrders.map((o) => o.order_name).join(", ")}`;

        if (fillMaterials) {
            try {
                const requested_products_payload = requested_products.map((p: any) => ({
                    product_id: p.id,
                    quantity: p.quantity,
                }));
                const res = await generateMaterialRequest({
                    sale_order_ids,
                    product_ids,
                    requested_products: requested_products_payload,
                });
                const mats = Array.isArray(res?.missing_materials) ? res.missing_materials : [];
                preselectedMaterials = mats.map((m: any) => ({
                    id: m.material_id,
                    material_code: m.material_code || null,
                    name: m.material_name || m.material_id,
                    measurement_unit: m.measurement_unit || "",
                    providers: [],
                    missing_quantity: Number(m.required_qty || 0),
                }));
            } catch (e) {
                console.error("Failed to generate material request", e);
                error("Không thể tính toán vật tư");
                return;
            }
        }

        // Build requested_products from latest edited quantities (use ref to avoid stale state)
        console.log("[MR] Step3 finishing latestProducts", latestProducts);
        console.log("[MR] Step3 requested_products payload", requested_products);
        const selected_sale_orders = saleOrders
            .filter((o: any) => selectedSaleOrderIds.has(o.id))
            .map((o: any) => ({ id: o.id, name: o.order_name }));
        console.log("[MR] Step3 selected_sale_orders", selected_sale_orders);
        navigate("/material-requests/create", {
            state: {
                preselectedMaterials,
                note,
                sale_order_ids,
                requested_products,
                selected_sale_orders,
            },
        });
        onClose();
    };

    const productSummary = useMemo(() => ({
        id: `Tổng ${selectedProducts.length} sản phẩm`,
        name: "Subtotal",
        isSummary: true,
    }), [selectedProducts.length]);

    const productTableItems = selectedProducts.length === 0 ? [] : [...selectedProducts, productSummary];

    const hasReducedItems = useMemo(() => selectedProducts.some((p: any) => Number(p.min_required_quantity || 0) > 0 && Number(p.production_quantity || 0) < Number(p.min_required_quantity || 0)), [selectedProducts]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogContentSpacious>
                <ModalTitle>Tạo phiếu lãnh từ sản phẩm</ModalTitle>
                <StepperStyled activeStep={active}>
                    <Step>
                        <StepLabel>Chọn sản phẩm</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Chọn đơn hàng</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>Xem lại</StepLabel>
                    </Step>
                </StepperStyled>

                {active === 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {isAllowToSearchProduct && <SearchBarWithSuggestion
                            menuList={products}
                            selectedItems={selectedProducts}
                            fetchItems={(variables: any) => fetchProducts({ ...variables })}
                            handleOnclickItem={handleOnclickProduct}
                        />}
                        <CustomTable
                            itemList={productTableItems}
                            RowElement={ProductRowStep}
                            TableColumns={ProductTableColumnsStep1}
                            extraRowProps={{
                                editable: false,
                                onRemove: isAllowToSearchProduct ? removeProduct : undefined,
                                ...(initialProducts ? { handleClickRow: handleClickOneProductAndGoNext } : {}),
                            }}
                            containerStyle={{ maxHeight: "400px" }}
                        />
                        <ButtonRow>
                            <StyledButton variant="text" onClick={onClose}>Đóng</StyledButton>
                            {!initialProducts && (
                                <StyledButton variant="contained" onClick={() => goNext()}
                                              disabled={selectedProducts.length === 0}>
                                    Tiếp theo
                                </StyledButton>
                            )}
                        </ButtonRow>
                    </div>
                )}

                {active === 1 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {saleOrders.length === 0 && (
                            <InfoText>
                                Không tìm thấy đơn hàng phù hợp cho sản phẩm đã chọn.
                            </InfoText>
                        )}
                        <CustomTable
                            itemList={saleOrders}
                            RowElement={SaleOrderRow}
                            TableColumns={[{ text: "", icon: <></> }, ...SaleOrderTableColumns]}
                            extraRowProps={{
                                selectedIds: selectedSaleOrderIds,
                                toggleRow: (id: any) => setSelectedSaleOrderIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(id)) next.delete(id); else next.add(id);
                                    return next;
                                }),
                            }}
                            containerStyle={{ maxHeight: "400px" }}
                        />
                        <ButtonRow>
                            <StyledButton variant="text" onClick={goBack}>Quay lại</StyledButton>
                            <StyledButton
                                variant="contained"
                                onClick={() => goNext()}
                                disabled={saleOrders.length === 0 || selectedSaleOrderIds.size === 0}
                            >
                                Tiếp theo
                            </StyledButton>
                        </ButtonRow>
                    </div>
                )}

                {active === 2 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, fontFamily: "Noto Sans" }}>
                        {hasReducedItems ? (
                            <TableWarningMessage style={{ fontFamily: "Noto Sans", fontSize: 14, lineHeight: "20px" }}>
                                Một số sản phẩm có số lượng sản xuất nhỏ hơn nhu cầu.
                            </TableWarningMessage>
                        ) : null}
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 700 }}>Đơn hàng đã chọn:</span>
                            {saleOrders
                                .filter((o) => selectedSaleOrderIds.has(o.id))
                                .map((o: any, idx: number, arr: any[]) => (
                                    <>
                                        <StyledNavLink key={o.id} to={`/product-orders/${o.id}`} target="_blank">
                                            {o.order_name}
                                        </StyledNavLink>
                                        {idx < arr.length - 1 ? <span>, </span> : null}
                                    </>
                                ))}
                        </div>
                        <CustomTable
                            itemList={productTableItems}
                            RowElement={ProductRowStep}
                            TableColumns={ProductTableColumnsStep3}
                            extraRowProps={{
                                editable: !lockedSaleOrder,
                                onRemove: isAllowToSearchProduct ? removeProduct : undefined,
                                onChangeQty: (id: string, qty: number) => {
                                    console.log("[MR] Step3 onChangeQty", { id, qty });
                                    setSelectedProducts((prev) => prev.map((p) => p.id === id ? {
                                        ...p,
                                        production_quantity: qty,
                                    } : p));
                                },
                            }}
                            containerStyle={{ maxHeight: "400px" }}
                        />
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Tooltip title="Tự động tính toán và điền vật tư còn thiếu từ sản phẩm đã chọn">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={fillMaterials}
                                            onChange={(e) => setFillMaterials(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Tự động điền vật tư từ sản phẩm"
                                    style={{ fontFamily: "Noto Sans", fontSize: 14 }}
                                />
                            </Tooltip>
                        </div>
                        <ButtonRow>
                            {!lockedSaleOrder && (
                                <StyledButton variant="text" onClick={goBack}>
                                    Quay lại
                                </StyledButton>
                            )}
                            <StyledButton variant="contained" onClick={finish}>Hoàn tất</StyledButton>
                        </ButtonRow>
                    </div>
                )}
            </DialogContentSpacious>
        </Dialog>
    );
};

export default CreateFromProductStepper;
